// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  callClaude,
  savedAnalysisIsRenderable,
  normalizeSavedAnalysis,
  analysisInterviewMessages,
} from "@/legacy/helpersModule";
import { Spin } from "@/shared/components/ui/Spin";
import { Button, Textarea } from "@/shared/components/ui";
import { exportAnalysisPdf } from "@/shared/pdf/exportPdf";
import { useEscapeKey } from "@/shared/hooks/useEscapeKey";
export function Analysis({ context, transcript, savedAnalysis, roleTabs, selectedApplicationId, onSelectApplication, onAnalysisGenerated, initialRemarks, onDecision, onBack, onLogout, hrDecisionAllowed = true, interviewIncomplete = false }) {
  const [data, setData] = useState(null), [busy, setBusy] = useState(true), [err, setErr] = useState(null), [remarks, setRemarks] = useState(initialRemarks || "");
  const [savingDecision, setSavingDecision] = useState(null); // "REJECTED" | "SHORTLISTED" | "POTENTIAL" | null
  useEscapeKey(onBack);

  // Runs the HR decision (Reject / Shortlist / Potential) with a visible
  // processing state so HR sees the backend work (save + async email) in flight.
  const runDecision = useCallback(
    async (status, payload) => {
      if (savingDecision) return;
      setSavingDecision(status);
      try {
        await onDecision(status, remarks, payload);
      } finally {
        // On success the page navigates away (unmount); on failure re-enable.
        setSavingDecision(null);
      }
    },
    [savingDecision, onDecision, remarks],
  );
  const autoClaudeConsumedRef = useRef(false);
  const tabs = roleTabs || [];
  const showRoleTabs = tabs.length > 0 && typeof onSelectApplication === "function";

  useEffect(() => {
    setRemarks(initialRemarks || "");
  }, [initialRemarks, selectedApplicationId]);

  useEffect(() => {
    const noTx = !transcript || !transcript.length;
    if (noTx) {
      setBusy(false);
      setErr(null);
      setData({
        summary: interviewIncomplete
          ? "This interview ended early (incomplete). The partial transcript and any auto-generated scores are not valid, so they are not shown. HR decision unlocks only after a completed interview for this role."
          : "No AI interview transcript is stored for this role yet. Complete or schedule a voice interview to generate automated scores.",
        tech: null,
        comm: null,
        rec: "Pending review",
        strengths: [],
        areas: [],
        noTranscript: true,
        pendingManualGenerate: false,
      });
      return undefined;
    }

    if (savedAnalysisIsRenderable(savedAnalysis)) {
      setBusy(false);
      setErr(null);
      setData({ ...normalizeSavedAnalysis(savedAnalysis), pendingManualGenerate: false });
      return undefined;
    }

    if (!autoClaudeConsumedRef.current) {
      autoClaudeConsumedRef.current = true;
      let cancelled = false;
      setBusy(true);
      setErr(null);
      setData(null);
      (async () => {
        try {
          const r = await callClaude(analysisInterviewMessages(transcript, context.jd.title), "Return only JSON.", true);
          if (!cancelled) {
            if (r) {
              setData({ ...r, noTranscript: false, pendingManualGenerate: false });
              if (typeof onAnalysisGenerated === "function") onAnalysisGenerated(r);
            } else setErr("Could not parse.");
          }
        } catch (e) {
          if (!cancelled) setErr(e.message);
        } finally {
          if (!cancelled) setBusy(false);
        }
      })();
      return () => { cancelled = true; };
    }

    setBusy(false);
    setErr(null);
    setData({
      summary:
        "AI summary has not been generated for this role yet. Use “Generate AI summary” below (optional).",
      tech: null,
      comm: null,
      rec: "Pending review",
      strengths: [],
      areas: [],
      noTranscript: false,
      pendingManualGenerate: true,
    });
    return undefined;
  }, [JSON.stringify(transcript), context.jd.title, JSON.stringify(savedAnalysis), selectedApplicationId, interviewIncomplete]);

  const handleManualGenerate = async () => {
    if (!transcript || !transcript.length) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await callClaude(analysisInterviewMessages(transcript, context.jd.title), "Return only JSON.", true);
      if (r) {
        setData({ ...r, noTranscript: false, pendingManualGenerate: false });
        if (typeof onAnalysisGenerated === "function") onAnalysisGenerated(r);
      } else setErr("Could not parse.");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (busy) return <div className="min-h-dvh safe-bottom bg-surface-alt flex items-center justify-center"><Spin label="Generating analysis…"/></div>;
  if (!data) return <div className="min-h-dvh safe-bottom bg-surface-alt flex items-center justify-center"><div className="text-center p-8 bg-white rounded-2xl"><p className="text-red-500 mb-4 text-sm">{err}</p><div className="flex items-center justify-center gap-2"><Button type="button" onClick={handleManualGenerate}>Retry</Button><Button type="button" variant="secondary" onClick={onBack}>Back</Button></div></div></div>;
  const recC = { "Strong Hire": "bg-green-500", "Hire": "bg-blue-500", "Weak Hire": "bg-amber-500", "No Hire": "bg-red-500", "Pending review": "bg-slate-500" };
  const decisionPayload = (() => {
    const { pendingManualGenerate: _p, ...rest } = data;
    return { ...rest, noTranscript: Boolean(data.noTranscript) };
  })();
  return (
    <div className="min-h-dvh safe-bottom bg-surface-alt">
      <div className="bg-brand-600 text-white px-4 sm:px-6 py-5 sm:py-8 safe-top safe-x">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-2xl sm:text-3xl font-black mb-1 break-words">{context.candidateName}</h2>
            <p className="text-white/80 text-sm">{context.jd.title}</p>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <span className={`px-4 py-2 rounded-xl text-white font-black text-sm text-center ${recC[data.rec] || "bg-blue-500"}`}>{data.rec}</span>
            {!data.noTranscript && !data.pendingManualGenerate ? (
              <button type="button" onClick={() => exportAnalysisPdf({ candidateName: context.candidateName, jobTitle: context.jd.title, data })} className="px-4 py-2 bg-white/15 hover:bg-white/25 text-white font-bold rounded-xl text-sm w-full sm:w-auto">⬇ Export PDF</button>
            ) : null}
            <button onClick={onLogout} className="text-white/70 hover:text-white text-sm py-2 sm:py-0 text-left sm:text-center">Logout</button>
          </div>
        </div>
      </div>
      {showRoleTabs ? (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-4 safe-x flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button
              key={t.applicationId}
              type="button"
              onClick={() => onSelectApplication(t.applicationId)}
              className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${selectedApplicationId === t.applicationId ? "bg-brand-600 text-white border-brand-600 shadow-md" : "bg-white text-ink-600 border-line hover:border-brand-300"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      ) : null}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 safe-x space-y-6">
        {data.pendingManualGenerate ? (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-amber-950">No AI summary stored for this role yet. Generate one to populate scores and narrative (optional).</p>
            <Button type="button" onClick={handleManualGenerate} loading={busy} className="whitespace-nowrap shrink-0">Generate AI summary</Button>
          </div>
        ) : null}
        {err ? <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-sm text-red-700">{err}</div> : null}
        <div className="bg-white rounded-2xl border border-line p-4 sm:p-6"><h3 className="text-xs font-black text-ink-400 uppercase mb-3">Summary</h3><p className="text-ink-600 text-sm sm:text-base">{data.summary}</p></div>
        {data.tech != null && data.comm != null ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{[["Technical", data.tech, "#b61e42"], ["Communication", data.comm, "#14b8a6"]].map(([l, v, col]) => <div key={l} className="bg-white rounded-2xl border border-line p-4 sm:p-5"><p className="text-xs font-bold text-ink-400 mb-3">{l}</p><div className="flex items-center gap-3"><div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${v * 10}%`, backgroundColor: col }}/></div><span className="font-black text-ink-900 text-lg">{v}/10</span></div></div>)}</div>
        ) : null}
        {(data.strengths || []).length > 0 || (data.areas || []).length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-white rounded-2xl border border-line p-4 sm:p-6"><h3 className="text-xs font-black text-green-600 uppercase mb-4">Strengths</h3><ul className="space-y-2">{(data.strengths || []).map((s, i) => <li key={i} className="flex gap-2 text-sm text-ink-600"><span className="text-green-500 font-bold shrink-0">•</span>{s}</li>)}</ul></div>
            <div className="bg-white rounded-2xl border border-line p-4 sm:p-6"><h3 className="text-xs font-black text-amber-600 uppercase mb-4">Improvements</h3><ul className="space-y-2">{(data.areas || []).map((a, i) => <li key={i} className="flex gap-2 text-sm text-ink-600"><span className="text-amber-500 font-bold shrink-0">•</span>{a}</li>)}</ul></div>
          </div>
        ) : null}
        {!hrDecisionAllowed ? (
          <div className="bg-slate-100 border border-line rounded-2xl p-5 text-sm text-ink-600">
            <p className="font-bold text-ink-900 mb-1">HR decision locked</p>
            <p className="text-ink-600">Reject / Shortlist unlocks only after the candidate completes the voice interview for this role (interviewed).</p>
          </div>
        ) : (
        <div className="bg-white rounded-2xl border border-line p-4 sm:p-6">
          <h3 className="text-xs font-black text-ink-400 uppercase mb-3">HR Decision</h3>
          {savingDecision ? (
            <div className="animate-pulse" aria-busy="true" aria-live="polite">
              <p className="text-sm font-semibold text-ink-600 mb-4">
                {savingDecision === "SHORTLISTED"
                  ? "Shortlisting candidate…"
                  : savingDecision === "REJECTED"
                    ? "Rejecting candidate…"
                    : "Moving to Potential Candidates…"}
              </p>
              <div className="h-20 bg-slate-100 rounded-control mb-4" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="h-12 bg-slate-100 rounded-control" />
                <div className="h-12 bg-slate-100 rounded-control" />
                <div className="h-12 bg-slate-100 rounded-control" />
              </div>
            </div>
          ) : (
            <>
              <Textarea rows={3} value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Final remarks…" className="mb-4"/>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                <button type="button" onClick={() => runDecision("REJECTED", decisionPayload)} className="py-3 border-2 border-red-100 text-red-600 font-bold rounded-control transition-colors hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-bad w-full">Reject</button>
                <button type="button" onClick={() => runDecision("POTENTIAL", decisionPayload)} className="py-3 border-2 border-indigo-200 text-indigo-700 font-bold rounded-control transition-colors hover:bg-indigo-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 w-full">Potential ★</button>
                <button type="button" onClick={() => runDecision("SHORTLISTED", decisionPayload)} className="py-3 bg-green-600 hover:bg-green-500 text-white font-bold rounded-control transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600 w-full">Shortlist ✓</button>
              </div>
            </>
          )}
        </div>
        )}
        <Button type="button" variant="secondary" fullWidth onClick={onBack}>← Back</Button>
      </div>
    </div>
  );
}

